import { createContext, useRef, useState } from "react";
import { Socket } from "socket.io-client";
import { getLocalUserId } from "../../../components/functions/utilities";
import { useSocket } from "../../../components/Hooks/useSocket";
import {
  Cards,
  Chart,
  PivotTable,
  User,
  DashboardDataPieces,
  PinnedVisualIdentifiersState,
  Report,
  PinnedVisual,
  ProgressStage,
  dashboardColdStates,
  presetTypes
} from "../../../interfaces/interfaces";
import { initialChannelState } from "../../ReportDetails/context/ReportContext";
import { channelState } from "../../ReportDetails/ReportDetails";
import { useSearchParams } from "react-router-dom";



type DashboardContextType = {
  client: string;
  isChannel: channelState;
  setIsChannel: (channelState) => void;
  socket: Socket;
  handleJoinRoom: (roomIds: string[]) => void;
  handleSetToggleMenuFunc: (
    value: (value: boolean) => void,
    createNewChat: () => void,
    chatToggle: (value: boolean) => void
  ) => void;
  toggleMenuRef: any;
  createNewChatRef: any;
  chatToggleRef: any;
  dataPieces: DashboardDataPieces;
  setDataPieces: any;
  companyUser: User[];
  setCompanyUser: any;
  ipLogos: {
    [x: string]: string;
  };
  setIpLogos: (value: any) => void;
  pinnedVisualIdentifiers: PinnedVisualIdentifiersState;
  setPinnedVisualIdentifiers: (data: PinnedVisualIdentifiersState) => void;
  allReportDetails: { [x: number]: Report };
  setAllReportDetails: (value: { [x: number]: Report }) => void;
  cardVisualOrder: PinnedVisual[];
  setCardVisualOrder: React.Dispatch<React.SetStateAction<PinnedVisual[]>>;
  currentCardOrder: PinnedVisual[];
  setCurrentCardOrder: React.Dispatch<React.SetStateAction<PinnedVisual[]>>;
  unpinItems: any[];
  setUnpinItems: React.Dispatch<React.SetStateAction<any[]>>;
  metrics: {
    sales: any;
    adSpend: any
    consolidatedRoas: any;
    consolidatedCards: any[]
  },
  setMetrics: React.Dispatch<React.SetStateAction<{
    sales: any;
    adSpend: any;
    consolidatedRoas: any;
    consolidatedCards: any[]
  }>>;
  metricsExtraConfig: {
    disconnectedIps: any[];
    connectedIps: any[];
    neverConnectedIps: any[];
    disconnectedFromBe: any[]
  },
  setMetricsExtraConfig: React.Dispatch<React.SetStateAction<{
    disconnectedIps: any[];
    connectedIps: any[];
    neverConnectedIps: any[];
    disconnectedFromBe: any[]
  }>>;
  topSellingProductsTable: any[];
  setTopSellingProductsTable: React.Dispatch<React.SetStateAction<any[]>>,
  hideDropdownOnScroll: boolean;
  setHideDropdownOnScroll: (value: boolean) => void;
  customDPJobId: number;
  setCustomDPJobId: any;
  progress: number;
  setProgress: (data: number) => void;
  progressStage: ProgressStage;
  setProgressStage: (data: ProgressStage) => void;
  temporaryDashboardConfig: {
    isInprogress: boolean;
    isTemporary: boolean;
    isMetricInProgress: boolean,
    isDashboardDpInProgress: boolean,
  },
  setTemporaryDashboardConfig: React.Dispatch<React.SetStateAction<{
    isInprogress: boolean;
    isTemporary: boolean;
    isMetricInProgress: boolean,
    isDashboardDpInProgress: boolean,
  }>>;
  dashboardDataPeriod: {
    type: number,
    startDate: string,
    endDate: string,
    enumType?: presetTypes,
    eventKey?: string,
    eventName?: string,
    matchDayOfWeek?: boolean,
  },
  setDashboardDataPeriod: React.Dispatch<React.SetStateAction<{
    type: number;
    startDate: string;
    endDate: string;
  }>>
  dashboardDataLoading: {
    isMetricLoading: boolean,
  },
  setDashboardDataLoading: React.Dispatch<React.SetStateAction<{
    isMetricLoading: boolean,
  }>>
};

export const DashboardContext = createContext<DashboardContextType | null>(null);

const DashboardProvider = ({ children }) => {
  const userId = getLocalUserId();
  const [isChannel, setIsChannel] = useState<channelState>(initialChannelState);
  const { socket, handleJoinRoom } = useSocket(userId.toString());
  const [pinnedVisualIdentifiers, setPinnedVisualIdentifiers] = useState<PinnedVisualIdentifiersState>();
  const toggleMenuRef = useRef(null);
  const createNewChatRef = useRef(null);
  const chatToggleRef = useRef(null);
  const [queryParameter] = useSearchParams();
  const [dataPieces, setDataPieces] = useState<DashboardDataPieces>({
    pinnedCharts: [],
    charts: [],
    pinnedPivotTables: [],
    pivotTables: [],
    pinnedCards: [],
    pinnedCohorts: [],
  });
  const [cardVisualOrder, setCardVisualOrder] = useState<PinnedVisual[]>([]);
  const [companyUser, setCompanyUser] = useState<User[]>([]);
  const handleSetToggleMenuFunc = (
    toggleMenu: (value: boolean) => void,
    createNewChat: () => void,
    chatToggle: (value: boolean) => void
  ) => {
    toggleMenuRef.current = toggleMenu;
    createNewChatRef.current = createNewChat;
    chatToggleRef.current = chatToggle;
  };
  const [ipLogos, setIpLogos] = useState({});
  const [allReportDetails, setAllReportDetails] = useState({});
  const [currentCardOrder, setCurrentCardOrder] = useState();
  const [unpinItems, setUnpinItems] = useState([]);
  const [metrics, setMetrics] = useState({
    sales: null,
    adSpend: null,
    consolidatedRoas: null,
    consolidatedCards: null
  });
  const [metricsExtraConfig, setMetricsExtraConfig] = useState({
    disconnectedIps: [],
    connectedIps: [],
    neverConnectedIps: [],
    disconnectedFromBe: []
  });
  const [hideDropdownOnScroll, setHideDropdownOnScroll] = useState(false);

  const [topSellingProductsTable, setTopSellingProductsTable] = useState<PivotTable[]>([]);
  const [customDPJobId, setCustomDPJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [progressStage, setProgressStage] = useState(ProgressStage.stage_0);

  const [dashboardDataPeriod, setDashboardDataPeriod] = useState({
    type: null,
    startDate: null,
    endDate: null,
    enumType: null,
    eventKey: null,
    eventName: null,
    matchDayOfWeek: false,
  });

  const [temporaryDashboardConfig, setTemporaryDashboardConfig] = useState({
    isInprogress: false,
    isTemporary: false,
    isMetricInProgress: false,
    isDashboardDpInProgress: false,
  });

  const [dashboardDataLoading, setDashboardDataLoading] = useState({
    isMetricLoading: false,
  })

  return (
    <DashboardContext.Provider
      value={{
        client: queryParameter.get("client"),
        ipLogos,
        setIpLogos,
        isChannel,
        setIsChannel,
        socket,
        handleJoinRoom,
        toggleMenuRef,
        chatToggleRef,
        createNewChatRef,
        handleSetToggleMenuFunc,
        dataPieces,
        setDataPieces,
        companyUser,
        setCompanyUser,
        setPinnedVisualIdentifiers,
        pinnedVisualIdentifiers,
        allReportDetails,
        setAllReportDetails,
        cardVisualOrder,
        setCardVisualOrder,
        currentCardOrder,
        setCurrentCardOrder,
        unpinItems,
        setUnpinItems,
        metrics,
        setMetrics,
        metricsExtraConfig,
        setMetricsExtraConfig,
        topSellingProductsTable,
        setTopSellingProductsTable,
        hideDropdownOnScroll,
        setHideDropdownOnScroll,
        customDPJobId,
        setCustomDPJobId,
        progress,
        setProgress,
        progressStage,
        setProgressStage,
        temporaryDashboardConfig,
        setTemporaryDashboardConfig,
        dashboardDataPeriod,
        setDashboardDataPeriod,
        dashboardDataLoading,
        setDashboardDataLoading
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

export default DashboardProvider;
